import React, {useEffect} from 'react';
import {Box, Checkbox, Fab, FormControl, Grid, TextField, Typography} from "@mui/material";
import {translate} from "../../../translate/translate";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import {Add, CachedRounded, Inventory2Outlined, Search, UnarchiveOutlined} from "@mui/icons-material";
import {CustomTabPanel} from "../../../utils/custom-tab-panel";
import TableActionComponent from "../../../components/table/table-action.component";
import TablePaginationComponent from "../../../components/table/table-pagination.component";
import {PoMonitoringService} from "../../../services/po-monitoring-service";
import {ErrorComponent} from "../../../components/error/error.component";
import {PaginatedResult} from "../../../models/default/paginated-result";
import {usePagination} from "../../../utils/pagination";
import {ConfirmationPoMonitoringNre} from "../../../models/basic/po-monitoring";
import Button from "@mui/material/Button";
import GenericDialog from "../../../components/dialog/generic-dialog.component";
import {toast} from "react-toastify";
import IconButton from "@mui/material/IconButton";

function ImportConfirmationMonitoringPo() {

    const service = new PoMonitoringService();

    const [tab, setTab] = React.useState(0);
    const [loading, setLoading] = React.useState<boolean>(true);

    const [confirmationNreList, setConfirmationNreList] = React.useState<PaginatedResult<ConfirmationPoMonitoringNre>>();
    const {
        dataSource: dataSourceConfirmationNre,
        pagination: paginationConfirmationNre,
        resetPage: resetPageConfirmationNre,
        handleChangePage: handleChangePageConfirmationNre,
        handleChangeRowsPerPage: handleChangeRowsPerPageConfirmationNre,
    } = usePagination<ConfirmationPoMonitoringNre>(confirmationNreList);

    const [selectedConfirmationNre, setSelectedConfirmationNre] = React.useState<ConfirmationPoMonitoringNre[]>([]);
    const [search, setSearch] = React.useState<string>("");
    const [open, setOpen] = React.useState<boolean>(false);

    const [openRevert, setOpenRevert] = React.useState<boolean>(false);
    const [selectedRevert, setSelectedRevert] = React.useState<ConfirmationPoMonitoringNre | null>(null);

    const handleChangeTab = (event: React.SyntheticEvent, newValue: number) => {
        setTab(newValue);
    };

    useEffect(() => {
        load(true);
    }, []);

    useEffect(() => {
        setConfirmationNreList([] as any);
        load(true);
    }, [tab]);

    useEffect(() => {
        load(true);
    }, [paginationConfirmationNre]);

    const load = (initialize = false) => {
        setLoading(true);
        if (tab === 0) {
            service.confirmationNre(paginationConfirmationNre, search).then(res => {
                setConfirmationNreList(res.data);
                initialize && setSelectedConfirmationNre(res.data.results);
            }).catch((error: any) => {
                ErrorComponent(error);
            }).finally(() => {
                setLoading(false);
            });
        } else if (tab === 1) {
            service.confirmationNreRemoved(paginationConfirmationNre, search).then(res => {
                setConfirmationNreList(res.data);
            }).catch((error: any) => {
                ErrorComponent(error);
            }).finally(() => {
                setLoading(false);
            });
        }
    }

    const changeSelectedConfirmationNre = (row: ConfirmationPoMonitoringNre) => {
        const isSelected = selectedConfirmationNre.some((item) => item.id === row.id);
        let updatedSelection: ConfirmationPoMonitoringNre[] = [];

        if (isSelected) {
            updatedSelection = selectedConfirmationNre.filter((item) => item.id !== row.id);
        } else {
            updatedSelection = [...selectedConfirmationNre, row];
        }

        console.log("Updated Selection: ", updatedSelection);
        setSelectedConfirmationNre(updatedSelection);
    };

    const customCellRender = (column: string, index: number, row: any) => {
        const isSelected = selectedConfirmationNre.some((item) => item.id === row.id);
        if (column === "select") {
            return (
                <Checkbox
                    checked={isSelected}
                    onChange={() => changeSelectedConfirmationNre(row)}
                    color={isSelected ? 'primary' : 'error'}
                    sx={{
                        '& .MuiSvgIcon-root': {
                            fill: !isSelected ? '#f35e5e' : '',
                        }
                    }}
                />
            );
        }

        if (column === "supplier.name") {
            return textWithStrikethrough(row.supplier?.name || '', isSelected);
        }

        if (column === "purchase_date") {
            const date = new Date(row.purchase_date + 'T00:00:00');
            const formattedDate = date.toLocaleDateString('pt-BR');
            return textWithStrikethrough(formattedDate, isSelected);
        }

        if (column === "reverter") {
            return (
                <IconButton
                    id="btn_reverter"
                    color="inherit"
                    aria-label="reverter"
                    edge="start"
                    onClick={() => {
                        console.log(row);
                        setSelectedRevert(row);
                        setOpenRevert(true);
                    }}
                >
                    <CachedRounded />
                </IconButton>
            );
        }

        return textWithStrikethrough(row[column] || '', isSelected);
    };

    const textWithStrikethrough = (text: string, selected = true) => {
        return !selected ? (
            <span style={{ textDecoration: 'line-through', color: 'gray' }}>
                {text || '-'}
            </span>
        ) : (
            <span>
                {text || '-'}
            </span>
        )
    }

    const customHeadRender = (column: string) => {
        if (column === "select") {
            return (
                <Checkbox
                    indeterminate={selectedConfirmationNre.length > 0 && selectedConfirmationNre.length < dataSourceConfirmationNre.length}
                    checked={dataSourceConfirmationNre.length > 0 && selectedConfirmationNre.length === dataSourceConfirmationNre.length}
                    onChange={(event) => {
                        if (event.target.checked) {
                            setSelectedConfirmationNre(dataSourceConfirmationNre);
                        } else {
                            setSelectedConfirmationNre([]);
                        }
                    }}
                />
            );
        }
    }

    const handleSubmit = () => {
        setLoading(true);
        const obj: {id: number, confirmed_as_nre: boolean}[] = [
            ...dataSourceConfirmationNre.map(item => (
                {
                    id: item.id, confirmed_as_nre: selectedConfirmationNre.some(it => it.id === item.id)
                }
            ))
        ]
        service.confirmAsNre(obj).then(res => {
            setOpen(false);
            resetPageConfirmationNre();
            toast.success("Confirmação realizada com sucesso!");
            load(true);
        }).catch((error: any) => {
            ErrorComponent(error);
        }).finally(() => {
            setLoading(false);
        });
    }

    const handleSubmitRevert = () => {
        if (!selectedRevert) return;

        console.log(selectedRevert);

        setLoading(true);
        const obj: {id: number, confirmed_as_nre: boolean}[] = [
            {
                id: selectedRevert.id, confirmed_as_nre: true
            }
        ]

        service.confirmAsNre(obj).then(res => {
            setOpenRevert(false);
            resetPageConfirmationNre();
            toast.success("PO enviada para monitoramento com sucesso!");
            load(true);
        }).catch((error: any) => {
            ErrorComponent(error);
        }).finally(() => {
            setLoading(false);
        });
    }

    const searchComponent = () => {
        return (
            <>
                <Grid container spacing={2} alignItems="center">
                    <Grid width="380px">
                        <FormControl fullWidth>
                            <TextField
                                sx={{minWidth: "150px !important"}}
                                name="search"
                                label={""}
                                fullWidth
                                variant={"outlined"}
                                autoFocus={false}
                                value={search}
                                placeholder="Pesquisar por PR, PO ou Supplier/Vendor"
                                onChange={(event: React.ChangeEvent<{
                                    value: string
                                }>) => {
                                    setSearch(event.target.value);
                                }}
                            />
                        </FormControl>
                    </Grid>
                    <Grid>
                        <Fab
                            onClick={() => load(true)}
                            size="small"
                            type="submit"
                            color="secondary">
                            <Search/>
                        </Fab>
                    </Grid>
                </Grid>
            </>
        )
    }

    return (
        <section>
            <div className="floating-title" style={{display: "flex", flexDirection: "row", alignItems: "center"}}>
                <Typography variant={"h5"}>
                    {translate.t("confirmation_monitoring_po")}
                </Typography>
            </div>

            <Box bgcolor="white" margin="1rem auto" pb={1} pt={1}>
                <Box sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddinTop: '20px',
                }}>
                    <Grid>
                        <Tabs value={tab}
                              onChange={handleChangeTab}>
                            <Tab label={
                                <Grid display="flex" alignItems="center" gap="10px" color="secondary">
                                    <Inventory2Outlined/>CONFIRMAR POS NRE
                                </Grid>
                            } />
                            <Tab label={
                                <Grid display="flex" alignItems="center" gap="10px" color="secondary">
                                    <UnarchiveOutlined/>POS REMOVIDAS
                                </Grid>
                            } />
                        </Tabs>
                    </Grid>
                </Box>
                <CustomTabPanel value={tab} index={0}>
                    {searchComponent()}

                    <Typography variant="body1" mt={3} mb={1}>Confirmar quais POs importadas do SAP devem ser mantidas no sistema.</Typography>
                    <Typography variant="body1" mb={2}>As demais serão descartadas e não aparecerão nos relatórios de monitoramento.</Typography>

                    <TablePaginationComponent
                        state={dataSourceConfirmationNre}
                        displayedColumnsShow={["Select", "PR", "PO", "Plant", "Data Criação", "Supplier/Vendor"]}
                        displayedColumns={["select", "purchase_request", "number", "plant", "purchase_date", "supplier.name"]}
                        // numberColumns={["purchase_request", "number"]}
                        // integerColumns={["purchase_request", "number"]}
                        customColumns={["select", "purchase_request", "number", "plant", "purchase_date", "supplier.name"]}
                        dateColumns={["purchase_date"]}
                        actionsComponent={TableActionComponent}
                        customCellRender={customCellRender}
                        customHead={["select"]}
                        customHeadRender={customHeadRender}

                        count={confirmationNreList?.count}
                        next={confirmationNreList?.next}
                        previous={confirmationNreList?.previous}
                        page={paginationConfirmationNre.page}
                        onPageChange={handleChangePageConfirmationNre}
                        rowsPerPage={paginationConfirmationNre.limit}
                        handleChangeRowsPerPage={handleChangeRowsPerPageConfirmationNre}
                        rowsPerPageOptions={[10, 50, 100]}
                        loading={loading}
                    />

                    <Box display="flex" justifyContent="flex-end" mt={2} gap={2} mr={2} p={3}>
                        <Button
                            color="secondary"
                            variant="contained"
                            onClick={() => {
                                setOpen(true);
                            }}>
                            {translate.t('confirm').toUpperCase()}
                        </Button>
                    </Box>
                </CustomTabPanel>

                <CustomTabPanel value={tab} index={1}>
                    {searchComponent()}

                    <Typography variant="body1" mt={3} mb={2}>Histórico de POs removidas do monitoramento de NRE</Typography>

                    <TablePaginationComponent
                        state={dataSourceConfirmationNre}
                        displayedColumnsShow={["PR", "PO", "Plant", "Data Criação", "Supplier/Vendor", "Reverter"]}
                        displayedColumns={["purchase_request", "number", "plant", "purchase_date", "supplier_name", "reverter"]}
                        dateColumns={["purchase_date"]}
                        customColumns={["reverter"]}
                        customCellRender={customCellRender}
                        actionsComponent={TableActionComponent}

                        count={confirmationNreList?.count}
                        next={confirmationNreList?.next}
                        previous={confirmationNreList?.previous}
                        page={paginationConfirmationNre.page}
                        onPageChange={handleChangePageConfirmationNre}
                        rowsPerPage={paginationConfirmationNre.limit}
                        handleChangeRowsPerPage={handleChangeRowsPerPageConfirmationNre}
                        rowsPerPageOptions={[10, 50, 100]}
                        loading={loading}
                    />
                </CustomTabPanel>
            </Box>

            <GenericDialog
                open={open}
                onClose={() => setOpen(false)}
                title={"confirm_sending_po"}
                colorButton={"primary"}
                labelButton={"confirm_sending"}
                handleConfirm={() => handleSubmit()}
                maxWidth={"sm"}
                disableConfirm={loading}
            >
                <Typography variant="body2">
                    Deseja realmente enviar as POs selecionadas para o Monitoramento NRE?
                </Typography>
            </GenericDialog>


            <GenericDialog
                open={openRevert}
                onClose={() => setOpenRevert(false)}
                title={"send_po"}
                colorButton={"primary"}
                labelButton={"confirm_sending"}
                handleConfirm={() => handleSubmitRevert()}
                maxWidth={"sm"}
                disableConfirm={loading}
            >
                <Typography variant="body2">
                    Deseja realmente enviar a PO para o Monitoramento NRE?
                </Typography>
            </GenericDialog>
        </section>
    );
}

export default ImportConfirmationMonitoringPo;
